import { useState, useRef, useCallback, DragEvent, ChangeEvent, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Microscope, Upload, X, AlertTriangle, Camera, FileText, Image as ImageIcon,
} from "lucide-react";
import { api, type JobStatus } from "../api/client";
import JobProgressIndicator from "../components/JobProgressIndicator";

const REPORT_TYPES = [
  { v: "blood_test", l: "Blood test / pathology" },
  { v: "xray", l: "X-ray" },
  { v: "mri", l: "MRI" },
  { v: "ct", l: "CT scan" },
  { v: "ultrasound", l: "Ultrasound" },
  { v: "ecg", l: "ECG" },
  { v: "endoscopy", l: "Endoscopy" },
  { v: "biopsy", l: "Biopsy" },
  { v: "histology", l: "Histology" },
  { v: "pathology", l: "Pathology (general)" },
  { v: "other", l: "Other / auto-detect" },
];

const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
const ACCEPT_ATTR = ACCEPTED_MIME.join(",");
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MAX_FILES = 4;

type FileItem = {
  id: string;
  filename: string;
  media_type: string;
  size_bytes: number;
  data: string;           // base64, no data-URI prefix
  preview_url?: string;   // for images only
};

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ReportAnalysisPage() {
  const [reportType, setReportType] = useState("blood_test");
  const [audience, setAudience] = useState("clinician");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<FileItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [jobStatus, setJobStatus] = useState<JobStatus | "idle">("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs when files change or page unmounts
  useEffect(() => {
    return () => {
      files.forEach((f) => f.preview_url && URL.revokeObjectURL(f.preview_url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback(
    async (incoming: FileList | File[]) => {
      setError(null);
      const list = Array.from(incoming);
      if (files.length + list.length > MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files allowed.`);
        return;
      }
      const next: FileItem[] = [];
      for (const file of list) {
        if (!ACCEPTED_MIME.includes(file.type)) {
          setError(`"${file.name}" is not a supported file type. Use JPEG, PNG, WebP, GIF, or PDF.`);
          continue;
        }
        if (file.size > MAX_FILE_BYTES) {
          setError(`"${file.name}" is ${formatBytes(file.size)}. Files must be ≤ 2 MB.`);
          continue;
        }
        try {
          const data = await toBase64(file);
          next.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            filename: file.name,
            media_type: file.type,
            size_bytes: file.size,
            data,
            preview_url: file.type.startsWith("image/")
              ? URL.createObjectURL(file)
              : undefined,
          });
        } catch (e) {
          setError(e instanceof Error ? e.message : `Failed to read ${file.name}`);
        }
      }
      if (next.length) setFiles((prev) => [...prev, ...next]);
    },
    [files.length],
  );


  const removeFile = (id: string) => {
    setFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f?.preview_url) URL.revokeObjectURL(f.preview_url);
      return prev.filter((x) => x.id !== id);
    });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData.files || []);
    if (pasted.length) {
      e.preventDefault();
      addFiles(pasted);
    }
  };

  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  };

  const submit = async () => {
    if (files.length === 0 && !notes.trim()) {
      setError("Please upload at least one file or add some notes to analyse.");
      return;
    }
    setJobStatus("pending");
    setElapsedMs(0);
    setAnalysis(null);
    setError(null);
    try {
      const res = await api.reportAnalyse(
        {
          report_type: reportType,
          audience,
          notes: notes.trim(),
          files: files.map((f) => ({
            media_type: f.media_type,
            data: f.data,
            filename: f.filename,
          })),
        },
        (p) => {
          setJobStatus(p.status);
          setElapsedMs(p.elapsed_ms);
        },
      );
      setAnalysis(res.analysis);
      setJobStatus("completed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setJobStatus("failed");
    }
  };

  const isWorking = jobStatus === "pending" || jobStatus === "running";


  return (
    <div className="space-y-6" onPaste={onPaste}>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Microscope className="text-nhs-darkblue" /> Report Analysis
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Upload blood tests, X-rays, MRI, CT, ultrasound, ECG, endoscopy, or pathology reports.
          JPEG, PNG, WebP, or PDF — up to 4 files, 2 MB each.
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label" htmlFor="report-type">Report type</label>
            <select
              id="report-type"
              className="input"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              disabled={isWorking}
            >
              {REPORT_TYPES.map((r) => (
                <option key={r.v} value={r.v}>{r.l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="audience">Audience</label>
            <select
              id="audience"
              className="input"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              disabled={isWorking}
            >
              <option value="clinician">Clinician</option>
              <option value="consumer">Consumer (plain English)</option>
            </select>
          </div>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver
              ? "border-nhs-blue bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          } ${isWorking ? "opacity-60 pointer-events-none" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <Upload className="mx-auto text-gray-400 mb-2" size={28} />
          <p className="text-sm text-gray-700 mb-2">
            Drag and drop files here, paste a screenshot (Ctrl/Cmd + V), or
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => inputRef.current?.click()}
              disabled={isWorking}
            >
              <Upload size={14} className="inline mr-1" /> Choose files
            </button>
            <button
              type="button"
              className="btn-secondary text-sm md:hidden"
              onClick={() => cameraRef.current?.click()}
              disabled={isWorking}
            >
              <Camera size={14} className="inline mr-1" /> Take photo
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={onSelect}
            className="hidden"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onSelect}
            className="hidden"
          />
          <p className="text-xs text-gray-500 mt-3">
            JPEG, PNG, WebP, GIF, PDF · max {MAX_FILES} files · 2 MB each
          </p>
        </div>

        {files.length > 0 && (
          <ul className="mt-3 space-y-2">
            {files.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-3 bg-white border border-gray-200 rounded p-2"
              >
                {f.preview_url ? (
                  <img
                    src={f.preview_url}
                    alt={f.filename}
                    className="w-12 h-12 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded border border-red-200">
                    <FileText size={20} className="text-red-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.filename}</p>
                  <p className="text-xs text-gray-500">
                    {f.media_type.startsWith("image/") ? (
                      <ImageIcon size={10} className="inline mr-1" />
                    ) : (
                      <FileText size={10} className="inline mr-1" />
                    )}
                    {f.media_type} · {formatBytes(f.size_bytes)}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${f.filename}`}
                  onClick={() => removeFile(f.id)}
                  disabled={isWorking}
                  className="text-gray-400 hover:text-red-600 p-1"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <label className="label mt-4" htmlFor="notes">Notes (optional)</label>
        <textarea
          id="notes"
          rows={4}
          className="input"
          placeholder="Any context — age, symptoms, relevant history, specific questions about the report."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isWorking}
          maxLength={5000}
        />

        <button
          onClick={submit}
          disabled={isWorking}
          className="btn-primary mt-3 w-full md:w-auto"
        >
          {isWorking ? "Analysing..." : "Analyse Report"}
        </button>
      </div>

      <JobProgressIndicator
        status={jobStatus}
        elapsedMs={elapsedMs}
        label="Analysing your report..."
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {analysis && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card">
          <h2 className="font-semibold mb-3">Analysis</h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800">
            {analysis}
          </div>
          <p className="text-xs text-gray-500 italic mt-4 pt-3 border-t">
            AI-generated analysis. Not a clinical diagnosis. Must be reviewed by a qualified
            healthcare professional. Contact your GP or NHS 111 for urgent concerns.
          </p>
        </motion.div>
      )}
    </div>
  );
}
